import React, {useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from 'react-redux';
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import Typography from "@mui/material/Typography";
import BoardTable from "components/atoms/BoardTable/BoardTable";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {Alert, Chip, Grid, Link, Skeleton} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {routes} from "routes";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import {Box} from "@mui/system";
import {deleteSimulation, selectAllSimulations} from "redux/simulation/simulationSlice";
import {selectAllSubscriptionsInactive, selectAllSubscriptionsLive} from "redux/subscription/subscriptionSlice";
import Tooltip from "@mui/material/Tooltip";
import SettingsDropdown from "components/molecules/SettingsDropdown/SettingsDropdown";
import {enqueueSnackbar} from 'notistack';
import {useConfirmation} from "utils/ConfirmationProvider";
import {
  formatCurrency,
  formatPercentage,
  getValueColor
} from "utils/metricFormatters";

// Helper function to safely convert values to numbers
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  // Handle both string and number inputs
  const numberValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};


export default function AutoTrade () {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation()
  const simulations = useSelector(selectAllSimulations);
  const subscriptionsInactive = useSelector(selectAllSubscriptionsInactive);
  const subscriptionsLive = useSelector(selectAllSubscriptionsLive);
  const strategies = useSelector(state => state.strategy.entities);
  const loading = useSelector(state => state.strategy.loading);

  const handleStopSimulation = async (simulationId, strategyName) => {
    confirm({
      title: `Stop simulating ${strategyName}`,
      message: <>Are you sure you want to turn off Simulated Trading for the strategy <b>{strategyName}</b>? <br></br>All historic performance data will be lost.</>,
      onConfirm: async () => {
        await dispatch(deleteSimulation(simulationId));
        enqueueSnackbar(`Simulation for "${strategyName}" has been stopped.`, { variant: 'success' })
      },
      maxWidth: "md"
    });
  }

  return (
    <>
      <PageHeader text1={"AutoTrade"} text2={"Control Panel"} />

      <Box sx={{mt: 5}}>
        <Typography variant="h3"> Currently AutoTrading </Typography>
        <Typography color={"secondary.dark"} fontWeight={500}>Strategies that are currently AutoTrading.</Typography>
      </Box>
      {_.isEmpty(simulations) && _.isEmpty(subscriptionsLive) ?
        <Box sx={{mt: 4}}>
          <Alert severity="info">
            You have no AutoTrading strategies. <br/>
            Please add strategies to <Link component={RouterLink} to={routes.dashboard}>Simulation</Link> or subscribe and activate AutoTrading.
          </Alert>
        </Box> :
        <BoardTable>
          <TableHead>
            <TableRow>
              <TableCell>Strategy</TableCell>
              <TableCell>Status</TableCell>
              <TableCell >Your Broker</TableCell>
              <TableCell align="right">Started</TableCell>
              <TableCell align="right">Strategy <br/>Model Account</TableCell>
              <TableCell align="right">Your  <br/>Scaling %</TableCell>
              <TableCell align="center" sx={{ width: 'auto'}}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>

            {subscriptionsLive.map((subscription, index) => {
              const strategy = strategies[subscription.strategy];
              // Get the scaling value from autotrading_data or use default value of 100%
              const scalingValue = subscription.autotrading_data && subscription.autotrading_data.autoTradeScaling 
                ? (parseFloat(subscription.autotrading_data.autoTradeScaling) * 100) + '%'
                : '100%';
              
              // Format the started date if it exists to YYYY-MM-DD format
              const startedDate = subscription.autotrading_started 
                ? new Date(subscription.autotrading_started).toISOString().split('T')[0]
                : 'N/A';
              
              // Get strategy value from performance metrics
              const strategyValue = strategy?.value;
              const formattedValue = formatCurrency(safeParseFloat(strategyValue), 0);

              return (
                strategy &&
                <TableRow >
                  <TableCell className="light2">
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                          <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                        </RouterLink>
                      </Grid>
                      <Grid item md={12} lg={6}>
                        <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}} mt={{xs: 1, lg: 0}}>
                          <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                            <Button color="primary" variant="outlined" size="small" > Details </Button>
                          </RouterLink>
                        </Box>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell >
                    <Chip label="LIVE" size="small" color="success"/>
                  </TableCell>

                  <TableCell>
                    {strategy.broker.name}
                  </TableCell>
                  <TableCell align="right" className="light">
                    <Typography variant="mediumText" >{startedDate}</Typography><br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText">{formattedValue}</Typography>
                    )}
                    <br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    <Typography variant="mediumText" >{scalingValue}</Typography><br/>
                  </TableCell>
                  <TableCell align="right" >
                    <SettingsDropdown>
                      <MenuItem component={RouterLink} to={routes.autoTradeFormEdit.replace(":strategyId", strategy.id)}>Edit AutoTrade</MenuItem>
                    </SettingsDropdown>

                  </TableCell>
                </TableRow>
              )})}

            {simulations.map((simulation, index) => {
              const strategy = strategies[simulation.strategy];
              // For simulations, use the scaling value from the simulation or default to 100%
              const scalingValue = simulation.scaling 
                ? (parseFloat(simulation.scaling) * 100) + '%' 
                : '100%';
              
              // Format the started date for simulations to YYYY-MM-DD format
              const startedDate = simulation.created 
                ? new Date(simulation.created).toISOString().split('T')[0]
                : 'N/A';
              
              // Get strategy value from performance metrics
              const strategyValue = strategy?.performance_metrics?.value;
              const formattedValue = formatCurrency(safeParseFloat(strategyValue), 0);

              return (
                strategy &&
                <TableRow >
                  <TableCell className="light2">
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                          <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                        </RouterLink>
                      </Grid>
                      <Grid item md={12} lg={6}>
                        <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}} mt={{xs: 1, lg: 0}}>
                          <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                            <Button color="primary" variant="outlined" size="small" > Details </Button>
                          </RouterLink>
                        </Box>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell >
                    <Chip label="SIMULATING" size="small" variant="x" color="secondary"/>
                  </TableCell>

                  <TableCell>
                    {strategy.broker.name}
                  </TableCell>
                  <TableCell align="right" className="light">
                    <Typography variant="mediumText" >{startedDate}</Typography><br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText">{formattedValue}</Typography>
                    )}
                    <br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    <Typography variant="mediumText" >{scalingValue}</Typography><br/>
                  </TableCell>
                  <TableCell align="right" >
                    <SettingsDropdown>
                      <MenuItem component={RouterLink} to={routes.strategyDetails.replace(":strategyId", strategy.id)}>Subscribe to Strategy</MenuItem>
                      <Tooltip title="You have to subscribe to enable AutoTrading" >
                        <span sx={{mb: 1}}> <MenuItem disabled>Change to Live &nbsp; (AutoTrade)</MenuItem> </span>
                      </Tooltip>
                      <Divider sx={{mt: 1, mb: 1}}/>
                      <MenuItem onClick={() => handleStopSimulation(simulation.id, strategy.name.toUpperCase())}>Stop Simulation</MenuItem>
                    </SettingsDropdown>

                  </TableCell>
                </TableRow>
              )})}

          </TableBody>
        </BoardTable>
      }


      <Box sx={{mt: 10}}>
        <Typography variant="h3"> Available for Live AutoTrading </Typography>
        <Typography color={"secondary.dark"} fontWeight={500}>Strategies that you have already subscribed to, but which you are not currently AutoTrading.</Typography>
      </Box>

      {_.isEmpty(subscriptionsInactive) ?
        <Box sx={{mt: 4}}>
          <Alert severity="info">
            You have no Inactive strategies. <br/>
            {!subscriptionsLive && <>Please subscribe to strategy to be able to activate AutoTrading.</>}
          </Alert>
        </Box> :

        <BoardTable>
          <TableHead>
            <TableRow>
              <TableCell>Strategy</TableCell>
              <TableCell width="10%"></TableCell>
              <TableCell align="right">Last 30 days</TableCell>
              <TableCell align="right">Last 60 days</TableCell>
              <TableCell align="right">Last 90 days</TableCell>
              <TableCell align="right">Annual Return</TableCell>
              <TableCell align="center" sx={{ width: 'auto'}}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptionsInactive.map((subscription, index) => {
              const strategy = strategies[subscription.strategy];
              
              // Get metrics from strategy
              const metrics = strategy?.performance_metrics || {};
              
              // Format metrics
              const annualReturn = formatPercentage(safeParseFloat(metrics.annual_return), 1);
              const return30d = formatPercentage(safeParseFloat(metrics.return_30d?.percentage), 1);
              const return60d = formatPercentage(safeParseFloat(metrics.return_60d?.percentage), 1);
              const return90d = formatPercentage(safeParseFloat(metrics.return_90d?.percentage), 1);
              
              // Get colors for values
              const annualReturnColor = getValueColor(safeParseFloat(metrics.annual_return));
              const return30dColor = getValueColor(safeParseFloat(metrics.return_30d?.percentage));
              const return60dColor = getValueColor(safeParseFloat(metrics.return_60d?.percentage));
              const return90dColor = getValueColor(safeParseFloat(metrics.return_90d?.percentage));

              return (
                strategy &&
                <TableRow >
                  <TableCell className="light2">
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                          <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                        </RouterLink>
                      </Grid>
                      <Grid item md={12} lg={6}>
                        <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}} mt={{xs: 1, lg: 0}}>
                          <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                            <Button color="primary" variant="outlined" size="small" > Details </Button>
                          </RouterLink>
                        </Box>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell></TableCell>

                 
                  <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText" color={return30dColor}>{return30d}</Typography>
                    )}
                    <br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText" color={return60dColor}>{return60d}</Typography>
                    )}
                    <br/>
                  </TableCell>
                  <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText" color={return90dColor}>{return90d}</Typography>
                    )}
                    <br/>
                  </TableCell>
                   <TableCell align="right" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="mediumText" color={annualReturnColor}>{annualReturn}</Typography>
                    )}
                    <br/>
                  </TableCell>

                  <TableCell align="right">
                    <RouterLink to={routes.autoTradeFormCreate.replace(":strategyId", strategy.id)}>
                      <Button variant="contained" color="secondary" >Set up autotrade </Button>
                    </RouterLink>
                  </TableCell>
                </TableRow>
              )})}

          </TableBody>
        </BoardTable>
      }
    </>
  )
}